import { Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  FiChevronRight,
  FiDollarSign,
  FiGift,
  FiMoreVertical,
  FiPieChart,
  FiTrendingUp,
  FiUserX,
} from 'react-icons/fi';
import Text from 'src/design/styleguide/text/Text';
import Hyperlink from 'src/design/components/hyperlink/Hyperlink';
import { propertiesSelector } from 'scripts/redux/selectors/properties';
import PropertyService from 'scripts/services/PropertyService';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import {
  breakpointSmall,
  breakpointXSmall,
} from 'src/design/styleguide/common/breakpoints';
import { advisorClientDetailUrl, advisorClientsUrl } from 'src/utils/pageUrls';
import {
  TOTAL_ROI_POPOVER_TEXT,
  TOTAL_VALUE_POPOVER_TEXT,
} from 'src/components/dashboard/portfolio/helpText';
import Popover from 'scripts/components/shared/Popover';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import { greenDark, pink50, pink700 } from 'src/themes/brickx/colors';
import { ClientDetailProvider } from './clientDetail/ClientDetailContext';
import { DETAIL_TABS } from './clientDetail/navigation';
import {
  formatCount,
  formatCurrency,
  formatDate,
  formatPercentage,
  formatStatus,
  getStatusTone,
  getValueTone,
} from './clientDetail/helpers';
import { IconButton, StatusPill } from './clientDetail/styles';

const Page = styled.main`
  display: grid;
  width: 100%;
  max-width: 1160px;
  min-width: 0;
  gap: ${gridUnit * 4}px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const Breadcrumb = styled.nav`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${gridUnit * 3}px;
  color: var(--bx-color---grey-700);
  font-size: 1.6rem;

  svg {
    flex: 0 0 auto;
  }
`;

const BreadcrumbLink = styled(Hyperlink)`
  color: ${themeForAdvisorAccount.colors.primary};
  font-weight: 600;

  &:hover,
  &:focus,
  &:active {
    color: ${themeForAdvisorAccount.colors.primary};
    outline: 0;
  }
`;

const BreadcrumbCurrent = styled.span`
  min-width: 0;
  overflow: hidden;
  color: var(--bx-color---grey-800);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IdentityHeader = styled.header`
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${gridUnit * 5}px;
`;

const IdentityMain = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${gridUnit * 6}px;
`;

const Avatar = styled.div`
  display: inline-flex;
  width: 96px;
  height: 96px;
  flex: 0 0 96px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--bx-color---purple-50);
  color: var(--bx-color---purple-600);
  font-size: 4rem;
  font-weight: 500;

  @media (max-width: 575px) {
    display: none;
  }
`;

const IdentityCopy = styled.div`
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${gridUnit * 3}px;
`;

const ClientName = styled(Text).attrs({
  as: 'h1',
  fontSize: 'large',
  fontWeight: 'bold',
})`
  margin: 0;
  color: var(--bx-color---grey-800);
  font-size: 3.2rem;
  line-height: 1.15;
`;

const ClientMeta = styled(Text).attrs({ as: 'p', fontSize: '4x-small' })`
  display: flex;
  flex-wrap: wrap;
  gap: ${gridUnit * 2}px;
  margin: ${gridUnit * 2}px 0 0;
  color: var(--bx-color---grey-700);
  line-height: 1.45;
`;

const ClientSince = styled(ClientMeta)`
  margin-top: ${gridUnit}px;
`;

const ActionMenu = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

const ActionMenuList = styled.div`
  position: absolute;
  z-index: 2;
  top: calc(100% + ${gridUnit}px);
  right: 0;
  width: max-content;
  box-sizing: border-box;
  padding: ${gridUnit}px;
  border: 1px solid var(--bx-color---grey-200);
  border-radius: var(--bx-radius);
  background: var(--bx-color---surface);
  box-shadow: var(--bx-shadow);
`;

const ActionMenuItem = styled.button`
  display: flex;
  width: 100%;
  height: 44px;
  align-items: center;
  gap: ${gridUnit * 3}px;
  padding: 0 ${gridUnit * 3}px;
  border: 0;
  border-radius: calc(var(--bx-radius) - 1px);
  background: transparent;
  color: var(--bx-color---grey-800);
  font-family: inherit;
  font-size: 1.6rem;
  font-weight: 700;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    color: ${pink700};
  }

  &:hover,
  &:focus {
    background: ${pink50};
    color: ${pink700};
    outline: 0;
  }
`;

const SummaryCards = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${gridUnit * 4}px;

  ${breakpointXSmall(`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `)}

  @media (min-width: 1250px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const MetricCard = styled.article`
  display: flex;
  min-width: 0;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: center;
  padding: ${gridUnit * 4}px;
  border: 1px solid var(--bx-color---grey-200);
  border-radius: var(--bx-radius);
  background: var(--bx-color---surface);
  box-shadow: var(--bx-shadow);

  ${breakpointSmall(`
    padding: ${gridUnit * 6}px;
  `)}

  @media (max-width: 575px) {
    align-items: center;
  }
`;

const MetricLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${gridUnit * 3}px;
  color: var(--bx-color---grey-800);
  font-size: 1.6rem;
  font-weight: 500;

  & > svg:first-child {
    width: 22px;
    height: 22px;
    flex: 0 0 22px;
    color: ${themeForAdvisorAccount.colors.primary};
  }

  @media (max-width: 575px) {
    gap: ${gridUnit * 2}px;
    text-align: center;

    & > svg:first-child {
      width: 20px;
      height: 20px;
      flex-basis: 20px;
    }
  }
`;

const MetricAmount = styled(Text).attrs({
  as: 'p',
  fontSize: 'small',
  fontWeight: 'bold',
})`
  margin: ${gridUnit * 4}px 0 0;
  color: var(--bx-color---grey-800);
  font-size: 2.8rem;
  font-weight: 700;
  line-height: 1;
  overflow-wrap: anywhere;

  &.positive {
    color: ${greenDark};
  }

  &.negative {
    color: ${pink700};
  }

  @media (max-width: 575px) {
    margin-top: ${gridUnit * 3}px;
    text-align: center;
  }
`;

const Tabs = styled.nav`
  display: flex;
  min-width: 0;
  overflow-x: auto;
  border-bottom: 1px solid var(--bx-color---grey-300);
  cursor: grab;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &.dragging {
    cursor: grabbing;
    user-select: none;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabButton = styled(Hyperlink)`
  position: relative;
  display: inline-flex;
  min-width: 128px;
  height: 68px;
  flex: 1 0 auto;
  align-items: center;
  justify-content: center;
  gap: ${gridUnit * 2}px;
  padding: 0 ${gridUnit * 4}px;
  border: 0;
  background: transparent;
  color: var(--bx-color---grey-800);
  font-family: inherit;
  font-size: 1.6rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;

  &::after {
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 3px;
    background: transparent;
    content: '';
  }

  &:hover,
  &:focus,
  &.active {
    color: ${themeForAdvisorAccount.colors.primary};
    text-decoration: none;
  }

  &:focus {
    outline: 0;
  }

  &:focus-visible {
    outline: 2px solid var(--bx-color---purple-300);
    outline-offset: -4px;
  }

  &.active::after {
    background: ${themeForAdvisorAccount.colors.primary};
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  min-width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  padding: 0 ${gridUnit}px;
  border-radius: 999px;
  background: var(--bx-color---purple-50);
  color: var(--bx-color---purple-600);
  font-size: 1.1rem;
  font-weight: 700;
  white-space: nowrap;
`;

const TabPanel = styled.div`
  min-width: 0;
`;

const SummaryMetricCard = ({ Icon, label, value, help, tone }) => (
  <MetricCard>
    <MetricLabel>
      <Icon aria-hidden="true" />
      <span>{label}</span>
      {help && <Popover placement="top" content={help} />}
    </MetricLabel>
    <MetricAmount className={tone}>{value}</MetricAmount>
  </MetricCard>
);

SummaryMetricCard.propTypes = {
  Icon: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  help: PropTypes.string,
  tone: PropTypes.string,
};

const useActivityProperties = (properties) => {
  const [propertyCatalog, setPropertyCatalog] = useState(null);
  useEffect(() => {
    if (properties && properties.length) return undefined;
    let cancelled = false;
    PropertyService.getPublishedProperties()
      .then((catalog) => {
        if (!cancelled) setPropertyCatalog(catalog || []);
      })
      .catch(() => {
        if (!cancelled) setPropertyCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [properties]);

  const hasProperties = properties && properties.length;
  return {
    properties: hasProperties ? properties : propertyCatalog || [],
    loading: !hasProperties && propertyCatalog === null,
  };
};

const PendingOrderCount = ({ tabId, count }) =>
  tabId === 'orders' && count > 0 ? (
    <CountBadge>{formatCount(count)}</CountBadge>
  ) : null;

PendingOrderCount.propTypes = {
  tabId: PropTypes.string.isRequired,
  count: PropTypes.number,
};

const DetailNavigation = ({
  clientId,
  activeTab,
  pendingOrderCount,
  onSelect,
}) => {
  const tabsRef = useRef(null);
  const dragState = useRef(null);
  const suppressClick = useRef(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const revealActiveTab = () => {
      const tabs = tabsRef.current;
      if (!tabs || tabs.scrollWidth <= tabs.clientWidth) return;
      const activeLink = tabs && tabs.querySelector('[aria-selected="true"]');
      if (!activeLink) return;
      const bounds = tabs.getBoundingClientRect();
      const selectedBounds = activeLink.getBoundingClientRect();
      if (selectedBounds.left < bounds.left) {
        tabs.scrollLeft += selectedBounds.left - bounds.left;
      } else if (selectedBounds.right > bounds.right) {
        tabs.scrollLeft += selectedBounds.right - bounds.right;
      }
    };
    revealActiveTab();
    window.addEventListener('resize', revealActiveTab);
    return () => window.removeEventListener('resize', revealActiveTab);
  }, [activeTab]);

  const startDragging = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    dragState.current = {
      startX: event.clientX,
      startScrollLeft: event.currentTarget.scrollLeft,
      moved: false,
    };
  };

  const dragTabs = (event) => {
    if (!dragState.current) return;
    const distance = event.clientX - dragState.current.startX;
    if (!dragState.current.moved && Math.abs(distance) > 4) {
      dragState.current.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setDragging(true);
    }
    if (dragState.current.moved) {
      event.currentTarget.scrollLeft =
        dragState.current.startScrollLeft - distance;
    }
  };

  const stopDragging = (event) => {
    if (!dragState.current) return;
    suppressClick.current = dragState.current.moved;
    dragState.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const selectTabFromKeyboard = (event, currentIndex) => {
    const keyToIndex = {
      Home: 0,
      End: DETAIL_TABS.length - 1,
      ArrowLeft: (currentIndex - 1 + DETAIL_TABS.length) % DETAIL_TABS.length,
      ArrowRight: (currentIndex + 1) % DETAIL_TABS.length,
    };
    const nextIndex = keyToIndex[event.key];
    if (nextIndex === undefined) return;

    event.preventDefault();
    const tabs =
      event.currentTarget.parentNode.querySelectorAll('[role="tab"]');
    tabs[nextIndex].focus();
    onSelect(DETAIL_TABS[nextIndex].id);
  };

  return (
    <Tabs
      ref={tabsRef}
      role="tablist"
      aria-label="Client detail sections"
      className={dragging ? 'dragging' : ''}
      onClickCapture={(event) => {
        if (!suppressClick.current) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick.current = false;
      }}
      onPointerDown={startDragging}
      onPointerMove={dragTabs}
      onPointerUp={stopDragging}
      onPointerCancel={(event) => {
        stopDragging(event);
        suppressClick.current = false;
      }}
    >
      {DETAIL_TABS.map(({ id, label }, index) => (
        <TabButton
          key={id}
          id={`client-detail-tab-${id}`}
          href={advisorClientDetailUrl(clientId, id)}
          draggable={false}
          role="tab"
          aria-selected={activeTab === id}
          aria-controls={`client-detail-${id}`}
          tabIndex={activeTab === id ? 0 : -1}
          className={activeTab === id ? 'active' : ''}
          onClick={(event) => {
            if (
              event.button > 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            ) {
              return;
            }
            event.preventDefault();
            onSelect(id);
          }}
          onKeyDown={(event) => selectTabFromKeyboard(event, index)}
        >
          {label}
          <PendingOrderCount tabId={id} count={pendingOrderCount} />
        </TabButton>
      ))}
    </Tabs>
  );
};

DetailNavigation.propTypes = {
  clientId: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
  pendingOrderCount: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
};

const SharedClientFrame = ({
  clientId,
  detail,
  activeTab,
  onRevokeConnection,
  onTabSelect,
}) => {
  const { identity, financials } = detail;
  const [actionsOpen, setActionsOpen] = useState(false);
  return (
    <Fragment>
      <Breadcrumb aria-label="Breadcrumb">
        <BreadcrumbLink href={advisorClientsUrl()}>Clients</BreadcrumbLink>
        <FiChevronRight aria-hidden="true" />
        <BreadcrumbCurrent>{identity.name}</BreadcrumbCurrent>
      </Breadcrumb>

      <IdentityHeader>
        <IdentityMain>
          <Avatar aria-hidden="true">{identity.initials || 'C'}</Avatar>
          <IdentityCopy>
            <NameRow>
              <ClientName>{identity.name}</ClientName>
              {identity.status && (
                <StatusPill className={getStatusTone(identity.status)}>
                  {formatStatus(identity.status)}
                </StatusPill>
              )}
            </NameRow>
            <ClientMeta>
              {identity.email && <span>{identity.email}</span>}
              {identity.email && identity.mobile && <span>•</span>}
              {identity.mobile && <span>{identity.mobile}</span>}
            </ClientMeta>
            {identity.clientSince && (
              <ClientSince>{`Client since ${formatDate(identity.clientSince)}`}</ClientSince>
            )}
          </IdentityCopy>
        </IdentityMain>
        <ActionMenu>
          <IconButton
            type="button"
            aria-label="Client actions"
            aria-haspopup="menu"
            aria-expanded={actionsOpen}
            onClick={() => setActionsOpen(!actionsOpen)}
          >
            <FiMoreVertical aria-hidden="true" />
          </IconButton>
          {actionsOpen && (
            <ActionMenuList role="menu">
              <ActionMenuItem
                type="button"
                role="menuitem"
                onClick={() => {
                  setActionsOpen(false);
                  onRevokeConnection();
                }}
              >
                <FiUserX aria-hidden="true" />
                Revoke connection
              </ActionMenuItem>
            </ActionMenuList>
          )}
        </ActionMenu>
      </IdentityHeader>

      <SummaryCards>
        <SummaryMetricCard
          Icon={FiPieChart}
          label="Portfolio Value"
          value={formatCurrency(financials.portfolioValue)}
          help={TOTAL_VALUE_POPOVER_TEXT}
        />
        <SummaryMetricCard
          Icon={FiDollarSign}
          label="Available Cash"
          value={formatCurrency(financials.availableBalance)}
          help="Funds currently available to trade across this client's plans."
        />
        <SummaryMetricCard
          Icon={FiTrendingUp}
          label="Total ROI"
          value={formatPercentage(financials.totalRoi)}
          help={TOTAL_ROI_POPOVER_TEXT}
          tone={getValueTone(financials.totalRoi)}
        />
        <SummaryMetricCard
          Icon={FiGift}
          label="Distributions"
          value={formatCurrency(financials.distributions)}
          help="Total distributions paid across the client's current portfolio."
        />
      </SummaryCards>

      <DetailNavigation
        clientId={clientId}
        activeTab={activeTab}
        pendingOrderCount={detail.pendingOrderCount}
        onSelect={onTabSelect}
      />
    </Fragment>
  );
};

SharedClientFrame.propTypes = {
  clientId: PropTypes.string.isRequired,
  detail: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  onRevokeConnection: PropTypes.func.isRequired,
  onTabSelect: PropTypes.func.isRequired,
};

const AdvisorClientDetailViewContent = ({
  clientId,
  detail,
  activeTab,
  downloadingDocumentKey,
  onDownloadDocument,
  onRevokeConnection,
  onTabSelect,
  properties,
  children,
}) => {
  const activityProperties = useActivityProperties(properties);
  return (
    <Page>
      <SharedClientFrame
        clientId={clientId}
        detail={detail}
        activeTab={activeTab}
        onRevokeConnection={onRevokeConnection}
        onTabSelect={onTabSelect}
      />
      <TabPanel
        id={`client-detail-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`client-detail-tab-${activeTab}`}
        tabIndex={0}
      >
        <ClientDetailProvider
          value={{
            clientId,
            detail,
            downloadingDocumentKey,
            onDownloadDocument,
            onTabSelect,
            properties: activityProperties.properties,
            propertiesLoading: activityProperties.loading,
          }}
        >
          {children}
        </ClientDetailProvider>
      </TabPanel>
    </Page>
  );
};

AdvisorClientDetailViewContent.propTypes = {
  clientId: PropTypes.string.isRequired,
  detail: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  downloadingDocumentKey: PropTypes.string,
  onDownloadDocument: PropTypes.func.isRequired,
  onRevokeConnection: PropTypes.func.isRequired,
  onTabSelect: PropTypes.func.isRequired,
  properties: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.element.isRequired,
};

const AdvisorClientDetailView = connect(propertiesSelector)(
  AdvisorClientDetailViewContent,
);

export { default as OverviewTab } from './clientDetail/OverviewTab';

export { default as PortfolioTab } from './clientDetail/PortfolioTab';

export { default as OrdersTab } from './clientDetail/OrdersTab';

export { default as TransactionsTab } from './clientDetail/TransactionsTab';

export { default as DocumentsTab } from './clientDetail/DocumentsTab';

export { default as AccountTab } from './clientDetail/AccountTab';

export { getPaginationItems } from './clientDetail/helpers';

export default AdvisorClientDetailView;
